import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Компонент с таблицей ставок НДС и пояснениями
 */
const VATRatesTable = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Данные о ставках НДС и категориях товаров/услуг
  const vatRatesData = [
    {
      rate: '20%',
      description: 'Основная ставка',
      examples: [
        'Большинство товаров и услуг',
        'Продажа недвижимости',
        'Строительные материалы',
        'Одежда и обувь',
        'Бытовая техника и электроника',
        'Мебель',
        'Услуги связи и интернет'
      ]
    },
    {
      rate: '10%',
      description: 'Льготная ставка',
      examples: [
        'Основные продовольственные товары (хлеб, молоко, масло и др.)',
        'Детские товары (одежда, обувь, кроватки, подгузники и др.)',
        'Периодические печатные издания и книги',
        'Лекарства и медицинские изделия',
        'Племенной скот'
      ]
    },
    {
      rate: '0%',
      description: 'Нулевая ставка (с правом вычета входного НДС)',
      examples: [
        'Экспорт товаров и услуг',
        'Международные перевозки',
        'Космическая деятельность',
        'Драгоценные металлы для Центробанка',
        'Товары для дипломатических представительств'
      ]
    },
    {
      rate: 'Без НДС',
      description: 'Освобождение от НДС (без права вычета входного НДС)',
      examples: [
        'Медицинские услуги',
        'Образовательные услуги',
        'Финансовые и страховые услуги',
        'Услуги по перевозке пассажиров городским транспортом',
        'Ритуальные услуги',
        'Услуги организаций культуры и искусства',
        'Продажа жилых помещений',
        'Предприятия на УСН, ЕНВД, патентной системе (с оборотом менее 2 млн руб. в квартал)'
      ]
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <button 
        className="w-full flex items-center justify-between text-lg font-medium text-gray-900 dark:text-white mb-2"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center">
          <FileText className="w-5 h-5 mr-2 text-green-500" />
          Ставки НДС в России
        </div>
        {isExpanded ? 
          <ChevronUp className="w-5 h-5 text-gray-500" /> : 
          <ChevronDown className="w-5 h-5 text-gray-500" />
        }
      </button>
      
      {isExpanded && (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            В России действуют следующие ставки налога на добавленную стоимость (НДС) в соответствии с Налоговым кодексом РФ:
          </p>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold text-left" style={{width: '15%'}}>Ставка</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-left" style={{width: '25%'}}>Описание</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-left">Примеры товаров и услуг</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {vatRatesData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium">{item.rate}</td>
                    <td className="px-4 py-3">{item.description}</td>
                    <td className="px-4 py-3">
                      <ul className="list-disc pl-5 space-y-1">
                        {item.examples.map((example, idx) => (
                          <li key={idx} className="text-sm">{example}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-2"><strong>Важное примечание:</strong></p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Ставки НДС могут меняться в соответствии с изменениями в законодательстве.</li>
              <li>При работе с НДС всегда рекомендуется консультироваться с бухгалтером или налоговым специалистом.</li>
              <li>Существуют особые правила расчета НДС для импортных товаров.</li>
              <li>Для экспортных операций действует ставка 0%, но требуется подтверждение вывоза товаров.</li>
              <li>Организации на специальных налоговых режимах (УСН, ЕНВД, ПСН) обычно освобождены от уплаты НДС.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default VATRatesTable;